import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { AmplitudeEvent } from "@common-shared/config/amplitude.config"
import { QuotaUsageType } from "@common-shared/config/oneVerify/oneVerifyCoverage.config"
import { COMMON_ONE_VERIFY_GENERAL_CONFIG } from "@common-shared/config/oneVerify/oneVerifyGeneral.config"

import {
    BULK_RESULTS_INSIGHTS_CONFIG,
    BulkPlanFinding,
    BulkResponsibilityBucket,
    BulkRunOutcome,
} from "../../../config/bulkResultsInsights.config"
import { AmplitudeHelper } from "../../../helpers/amplitude.helper"
import { BulkResultsInsightsHelper } from "../../../helpers/bulkResultsInsights.helper"
import { CheckerHelper } from "../../../helpers/checker.helper"
import { RuntimeHelper } from "../../../helpers/runtime.helper"
import { useAppDispatch } from "../../../store/appDispatch.hook"
import { useAppSelector } from "../../../store/selectors/app.selector"
import { checkerSelector } from "../../../store/selectors/checker.selector"
import { runtimeSelector } from "../../../store/selectors/runtime.selector"
import { selectedPracticeRoleSelector } from "../../../store/selectors/selectedPracticeRole.selector"
import { userDetailsSelector } from "../../../store/selectors/userDetails.selector"
import { checkerUpdateInputDataZipCodes } from "../../../store/slices/checker.slice"
import { checkerRestoreMultiplePoliciesSmartScan } from "../../../store/thunks/checker.thunks"
import type {
    IBulkResultsFilter,
    IBulkResultsInsightsModel,
} from "../../../types/bulkResultsInsights.types"
import type { IDynamicCheckerFormInputData } from "../../../types/data.types"
import { BulkResultsOutcomesComponent } from "./bulkResultsOutcomes.component"
import { BulkResultsPatientResponsibilityComponent } from "./bulkResultsPatientResponsibility.component"
import { BulkResultsPlanFindingsComponent } from "./bulkResultsPlanFindings.component"
import { BulkResultsRunSummaryComponent } from "./bulkResultsRunSummary.component"
import { BulkResultsSmartScanBannerComponent } from "./bulkResultsSmartScanBanner.component"
import { BulkResultsSmartScanPreparationComponent } from "./bulkResultsSmartScanPreparation.component"
import { BulkResultsTableComponent } from "./bulkResultsTable.component"

export const BulkResultsInsightsComponent: React.FunctionComponent<
    IBulkResultsInsightsComponentProps
> = props => {
    const dispatch = useAppDispatch()

    const checkerState = useAppSelector(checkerSelector)
    const runtimeState = useAppSelector(runtimeSelector)
    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)
    const { selectedPracticeUsageQuota } = useAppSelector(userDetailsSelector)

    const isSmartScanRunning = useMemo<boolean>(
        () => RuntimeHelper.isRestoreMultiplePoliciesSmartScanLoading(),
        [runtimeState.isLoading],
    )

    const isBulkRunLoading = useMemo<boolean>(
        () => RuntimeHelper.isBulkCoverageChecksLoading() || isSmartScanRunning,
        [isSmartScanRunning, runtimeState.isLoading],
    )

    const [filter, setFilter] = useState<IBulkResultsFilter | null>(null)
    const [isSmartScanPreparing, setIsSmartScanPreparing] = useState(false)

    // Snapshot taken when a scan starts, since inactive rows drop out as results land
    const [scanningCount, setScanningCount] = useState(0)

    const [visibleCount, setVisibleCount] = useState(
        BULK_RESULTS_INSIGHTS_CONFIG.resultsPageSize,
    )

    const isMedicaid = props.checkType === QuotaUsageType.MEDICAID

    const lastBuiltModelAtRef = useRef(Date.now())

    const buildCurrentModel = useCallback(() => {
        return BulkResultsInsightsHelper.buildModel(
            isMedicaid
                ? checkerState.medicaidChecks
                : checkerState.continuousMonitoringCoverageChecks,
            props.checkType,
            isMedicaid,
        )
    }, [
        checkerState.continuousMonitoringCoverageChecks,
        checkerState.medicaidChecks,
        isMedicaid,
        props.checkType,
    ])

    const [model, setModel] =
        useState<IBulkResultsInsightsModel>(buildCurrentModel)

    useEffect(() => {
        const rebuild = () => {
            lastBuiltModelAtRef.current = Date.now()
            setModel(buildCurrentModel())
        }

        // Flush as soon as the bulk run or SmartScan retry finishes so the
        // header and charts match "Run complete" without waiting out the interval
        if (!isBulkRunLoading) {
            rebuild()

            return
        }

        const delay = Math.max(
            0,
            BULK_RESULTS_INSIGHTS_CONFIG.modelUpdateIntervalMs -
                (Date.now() - lastBuiltModelAtRef.current),
        )

        const timeoutId = setTimeout(rebuild, delay)

        return () => {
            clearTimeout(timeoutId)
        }
    }, [buildCurrentModel, isBulkRunLoading])

    const filteredRows = useMemo(() => {
        return BulkResultsInsightsHelper.getFilteredRows(model.rows, filter)
    }, [filter, model.rows])

    // Inactive input lookup follows the throttled model, not every Redux check update
    const inactiveInputData = useMemo(() => {
        const inactiveRows = model.rows.filter(
            row => row.outcome === BulkRunOutcome.POLICY_INACTIVE,
        )

        return BulkResultsInsightsHelper.getInputDataForRows(
            isMedicaid
                ? checkerState.medicaidChecks
                : checkerState.continuousMonitoringCoverageChecks,
            inactiveRows,
        )
    }, [isMedicaid, model.rows])

    useEffect(() => {
        setVisibleCount(BULK_RESULTS_INSIGHTS_CONFIG.resultsPageSize)
    }, [filter])

    const toggleFilter = useCallback((nextFilter: IBulkResultsFilter) => {
        setFilter(current => {
            if (
                current &&
                current.kind === nextFilter.kind &&
                current.value === nextFilter.value &&
                current.subtype === nextFilter.subtype
            ) {
                return null
            }

            return nextFilter
        })
    }, [])

    const onOutcomeSelect = (outcome: BulkRunOutcome, label: string) => {
        toggleFilter({ kind: "outcome", value: outcome, label })
    }

    const onResponsibilitySelect = (
        bucket: BulkResponsibilityBucket,
        label: string,
    ) => {
        toggleFilter({ kind: "responsibility", value: bucket, label })
    }

    const onFindingSelect = (finding: BulkPlanFinding, label: string) => {
        toggleFilter({ kind: "finding", value: finding, label })
    }

    // Re-runs the inactive rows in place, so results update without leaving this page
    const onRunSmartScanRetry = (
        preparedInputData: IDynamicCheckerFormInputData[],
    ) => {
        if (
            !selectedPracticeRole ||
            !selectedPracticeUsageQuota ||
            !preparedInputData.length
        ) {
            return
        }

        CheckerHelper.runChecksWithQuotaConfirmation(
            selectedPracticeUsageQuota,
            () => {
                setIsSmartScanPreparing(false)
                setScanningCount(preparedInputData.length)

                AmplitudeHelper.logEvent(
                    AmplitudeEvent.SCAN_FROM_BULK_INSIGHTS,
                    {
                        practiceId: selectedPracticeRole.practice.id,
                        practiceName: selectedPracticeRole.practice.name,
                        checks: preparedInputData.length,
                        bulkCheckerRunId: checkerState.bulkCheckerRunId,
                    },
                )

                dispatch(
                    checkerUpdateInputDataZipCodes(
                        Object.fromEntries(
                            preparedInputData.map(inputData => [
                                inputData.id,
                                inputData.zipCode || "",
                            ]),
                        ),
                    ),
                )

                dispatch(
                    checkerRestoreMultiplePoliciesSmartScan({
                        payload: preparedInputData,
                        practice: selectedPracticeRole.practice,
                    }),
                )
            },
            QuotaUsageType.SMART_SCAN,
            preparedInputData.length,
        )
    }

    if (!model.completedCount) {
        return null
    }

    const selectedOutcome =
        filter?.kind === "outcome"
            ? (filter.value as BulkRunOutcome)
            : undefined

    const selectedResponsibility =
        filter?.kind === "responsibility"
            ? (filter.value as BulkResponsibilityBucket)
            : undefined

    const selectedFinding =
        filter?.kind === "finding"
            ? (filter.value as BulkPlanFinding)
            : undefined

    return (
        <div className={props.className}>
            <BulkResultsRunSummaryComponent
                className="mb-[32px]"
                model={model}
                isRunning={isBulkRunLoading}
            />

            <BulkResultsOutcomesComponent
                outcomes={model.outcomes}
                selectedOutcome={selectedOutcome}
                onSelect={onOutcomeSelect}
            />

            {model.inactiveCount || model.smartScanRecoveredCount ? (
                <BulkResultsSmartScanBannerComponent
                    inactiveCount={model.inactiveCount}
                    creditsPerCheck={
                        COMMON_ONE_VERIFY_GENERAL_CONFIG.quotaUsageAmounts[
                            QuotaUsageType.SMART_SCAN
                        ]
                    }
                    recoveredCount={model.smartScanRecoveredCount}
                    isRunning={isSmartScanRunning}
                    scanningCount={scanningCount}
                    onRunSmartScan={() => {
                        setIsSmartScanPreparing(true)
                    }}
                />
            ) : null}

            {isSmartScanPreparing && inactiveInputData.length ? (
                <BulkResultsSmartScanPreparationComponent
                    inputData={inactiveInputData}
                    onCancel={() => {
                        setIsSmartScanPreparing(false)
                    }}
                    onRun={onRunSmartScanRetry}
                />
            ) : null}

            {model.fullCoverageCount ? (
                <BulkResultsPatientResponsibilityComponent
                    className="mt-[40px]"
                    fullCoverageCount={model.fullCoverageCount}
                    stats={model.responsibility}
                    selectedBucket={selectedResponsibility}
                    onSelect={onResponsibilitySelect}
                />
            ) : null}

            <BulkResultsPlanFindingsComponent
                className="mt-[40px]"
                findings={model.findings}
                selectedFinding={selectedFinding}
                onSelect={onFindingSelect}
            />

            <BulkResultsTableComponent
                className="mt-[40px]"
                rows={filteredRows}
                totalPatients={model.completedCount}
                visibleCount={visibleCount}
                filter={filter}
                onClearFilter={() => {
                    setFilter(null)
                }}
                onLoadMore={() => {
                    setVisibleCount(
                        current =>
                            current +
                            BULK_RESULTS_INSIGHTS_CONFIG.resultsPageSize,
                    )
                }}
            />
        </div>
    )
}

interface IBulkResultsInsightsComponentProps {
    className?: string
    checkType: QuotaUsageType
}
